import {IconName} from '@lightricks/react-design-system';
import {ROUTES} from '@contentlab/config/routesConstants';
import {FeatureName} from '@contentlab/types/project';
import {getFeatureState, ContentLabFlags} from '@contentlab/utils/featureUtils';
import getFeatureIconName from '@contentlab/utils/getFeatureIconName';
import translate from '@/utils/translate';

export interface MenuItemConfig {
  id: string;
  path: string;
  label: string;
  iconName: IconName;
  matchPaths?: string[];
  featureFlag?: string;
  disabled?: boolean;
  tooltip?: string;
}

const getBaseMenuItems = (flags: ContentLabFlags): MenuItemConfig[] => {
  const reformattingState = getFeatureState(FeatureName.REFORMATTING, flags);
  const aiDubbingState = getFeatureState(FeatureName.AI_DUBBING, flags);
  const rephrasingState = getFeatureState(FeatureName.REPHRASING, flags);
  const captionsState = getFeatureState(FeatureName.CAPTIONS, flags);
  const upscaleState = getFeatureState(FeatureName.UPSCALE, flags);
  const aiAvatarState = getFeatureState(FeatureName.AI_AVATAR, flags);

  return [
    {
      id: 'home',
      path: ROUTES.CONTENT_LAB,
      label: translate('Home'),
      iconName: 'Navigation-Home-Stroke',
    },
    {
      id: 'resize',
      path: `${ROUTES.CONTENT_LAB}${ROUTES.CONTENT_LAB_RESIZE}`,
      label: translate('Resize'),
      iconName: getFeatureIconName(FeatureName.REFORMATTING),
      ...reformattingState,
    },
    {
      id: 'dub',
      path: `${ROUTES.CONTENT_LAB}${ROUTES.CONTENT_LAB_DUB}`,
      label: translate('Dub'),
      iconName: getFeatureIconName(FeatureName.AI_DUBBING),
      ...aiDubbingState,
    },
    {
      id: 'rephrase',
      path: `${ROUTES.CONTENT_LAB}${ROUTES.CONTENT_LAB_REPHRASE}`,
      label: translate('Rescript'),
      iconName: getFeatureIconName(FeatureName.REPHRASING),
      ...rephrasingState,
    },
    {
      id: 'captions',
      path: `${ROUTES.CONTENT_LAB}${ROUTES.CONTENT_LAB_CAPTIONS}`,
      label: translate('Captions'),
      iconName: getFeatureIconName(FeatureName.CAPTIONS),
      featureFlag: 'clCaptions',
      ...captionsState,
    },
    {
      id: 'ai-avatar',
      path: `${ROUTES.CONTENT_LAB}${ROUTES.CONTENT_LAB_AI_AVATAR}`,
      label: translate('Avatars'),
      iconName: getFeatureIconName(FeatureName.AI_AVATAR),
      featureFlag: 'clAiAvatar',
      ...aiAvatarState,
    },
    {
      id: 'upscale',
      path: `${ROUTES.CONTENT_LAB}${ROUTES.CONTENT_LAB_UPSCALE}`,
      label: translate('Upscale'),
      iconName: getFeatureIconName(FeatureName.UPSCALE),
      featureFlag: 'clUpscale',
      ...upscaleState,
    },
    {
      id: 'projects',
      path: `${ROUTES.CONTENT_LAB}${ROUTES.CONTENT_LAB_PROJECTS}`,
      label: translate('Projects'),
      iconName: 'Navigation-Projects',
      matchPaths: [`${ROUTES.CONTENT_LAB}${ROUTES.CONTENT_LAB_PROJECT}`],
    },
  ];
};

export function getMenuItemsList(
  flags: ContentLabFlags = {}
): MenuItemConfig[] {
  return getBaseMenuItems(flags).filter((item) => {
    // If no feature flag is specified, always show the item
    if (!item.featureFlag) {
      return true;
    }
    // Handle existing boolean flags for backward compatibility
    if (item.featureFlag === 'clCaptions') {
      return flags.clCaptions === true;
    }
    if (item.featureFlag === 'clAiAvatar') {
      return flags.clAiAvatar === true;
    }
    if (item.featureFlag === 'clUpscale') {
      return flags.clUpscale === true;
    }
    // For other flags, default to true (enabled)
    return true;
  });
}

// For backward compatibility
export const menuItemsList: MenuItemConfig[] = getBaseMenuItems({});
